import axios, { HttpStatusCode } from 'axios';

import config from '@/config';
import { ToastType } from '@/const';
import { routerInstance } from '@/main';
import { useToastStore } from '@/store';

axios.defaults.baseURL = config.apiUrl;
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';

axios.interceptors.request.use(config => {

  // If the data being sent in the request is form data, let the browser
  // automatically set the Content-Type header to multipart/form-data,
  // otherwise send it as application/json for all other requests
  if (config.data && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

axios.interceptors.response.use(response => {
  return response;
}, async err => {
  // Handle API call failures due to a problem with the user's internet connection
  if (!err.response) {
    const response = {
      statusCode: null,
      exception: 'An unknown error occurred',
    };

    return Promise.reject(response);
  }

  // Don't redirect in development for failed API requests. This makes it easier for debugging, because if there's a problem,
  // the developer doesn't have to navigate back to where the problem occurred before being able to debug it.
  if (import.meta.env.DEV) {
    return Promise.reject(err);
  }

  // Ensure routerInstance is available before using it
  if (!routerInstance) {
    console.error('Router instance not available in HttpClient interceptor');
    // Decide how to handle this - maybe reject differently or queue the navigation?
    // For now, just prevent crashing:
    return Promise.reject(err);
  }

  const toastStore = useToastStore();

  switch (err.response.status) {

    // Error response `404`
    case HttpStatusCode.NotFound:
      break;

      // Error response `50x`
    case HttpStatusCode.InternalServerError:
      toastStore.add(ToastType.Error, 'Server Error. There seems to be an issue with our server. Please try again later.');
      break;

      // If the server responds with a bad request error, don't do anything, because that is something that the application should handle.
      // If the server responds with an unauthorized error, don't do anything, because that is something that the application will handle.
      // If the server responds with a validation error, don't do anything, because the validation errors will be handled by the component that made the API request.
      // Error response `400`,`401`,`409`,`422` and `409` respectively
    case HttpStatusCode.BadRequest:
    case HttpStatusCode.Unauthorized:
    case HttpStatusCode.Conflict:
    case HttpStatusCode.UnprocessableEntity:
    case HttpStatusCode.TooManyRequests:
      break;

      // Error response `404`
    case HttpStatusCode.Forbidden:
      await routerInstance.push({ name: 'app_forbidden' });
      break;

    default:
      await routerInstance.push({ name: 'app_unknown_error' });
  }

  return Promise.reject(err);
});

const defaultHeaders = {
  'Content-Type': 'application/json',
};

export const HttpClient = {
  async request(method, url, body = null, queryParams = null, headers = null) {
    const axiosConfig = { method, url, headers, data: body, params: queryParams };

    try {
      const response = await axios(axiosConfig);

      return response.data;
    } catch (err) {
      if (err.response) {
        return err.response.data;
      }

      return { statusCode: null, exception: 'An unknown error occurred' };
    }
  },
  get(url, query, headers) {
    return this.request('GET', url, null, query, headers || defaultHeaders);
  },
  post(url, body, headers) {
    return this.request('POST', url, body, null, headers || defaultHeaders);
  },
  put(url, body, headers) {
    return this.request('PUT', url, body, null, headers || defaultHeaders);
  },
  delete(url, headers) {
    return this.request('DELETE', url, null, null, headers || defaultHeaders);
  },
};
